import { Box, SvgIconProps } from '@mui/material';
import { clsx } from 'clsx';
import { Alert, AlertDecagram, CheckCircle, CheckDecagramOutline, InformationOutline, Star } from 'mdi-material-ui';
import React, { forwardRef } from 'react';
import { useTheme } from '../../theme';
import { CommonDataProps, combineSx, SxProps } from '../../utils';
import { Tooltip } from '../Tooltip';

type NativeDivProps = Omit<React.ComponentPropsWithoutRef<'div'>, 'onClick'>;

export type BadgeVariant = 'default' | 'new' | 'info' | 'success' | 'warning' | 'error';

export interface BadgeProps extends NativeDivProps, CommonDataProps {
  /**
   * The content of the component.
   */
  label: string;
  /**
   * This prop isn't supported.
   */
  children?: null;
  /**
   * An override for the Icon when the "default" variant is used
   */
  Icon?: React.ElementType<SvgIconProps>;
  /** Whether to display an icon, defaults to true for medium and false for small badges */
  showIcon?: boolean;
  /**
   * The size of the component. Small badges do not have an icon.
   * @default 'medium'
   */
  size?: 'dot' | 'small' | 'medium';
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps;
  /**
   * Optional tooltip to display.
   */
  TooltipContent?: React.ReactNode;
  /**
   * Controls the color and icon.
   * @default 'default'
   */
  variant?: BadgeVariant;
}

const variantAllowsCustomIcons = ['info', 'default'];

export const Badge = forwardRef<HTMLDivElement, BadgeProps>(function Badge(
  {
    label,
    variant = 'default',
    size = 'medium',
    sx,
    TooltipContent,
    Icon,
    showIcon,
    'data-testid': dataTestId,
    'data-trackid': dataTrackId,
    ...props
  },
  ref
) {
  const { VariantIcon, color, background, fill } = useVariantProps(variant);
  const calculatedShowIcon = showIcon ?? size !== 'small';

  const BadgeIcon = Icon && variantAllowsCustomIcons.includes(variant) ? Icon : VariantIcon;

  const badge =
    size === 'dot' ? (
      <Box
        aria-label={label}
        sx={combineSx(
          {
            borderRadius: '6px',
            width: '12px',
            height: '12px',
            display: 'inline-flex',
            alignSelf: 'flex-start',
            backgroundColor: fill,
          },
          sx
        )}
        ref={ref}
        data-testid={dataTestId}
        data-trackid={dataTrackId}
      />
    ) : (
      <Box
        {...props}
        ref={ref}
        component="span"
        className={clsx('Badge', props.className)}
        data-testid={dataTestId}
        data-trackid={dataTrackId}
        sx={combineSx(
          (theme) => ({
            display: 'inline-flex',
            alignSelf: 'flex-start',
            alignItems: 'center',
            justifyContent: 'center',
            padding: theme.spacing(0.25, 1),
            borderRadius: theme.shape.borderRadius,
            background,
            color,
            gap: theme.spacing(0.5),
            width: 'auto',
            minHeight: '24px',
            ...theme.typography.body1,
            ...(size === 'small' && {
              minHeight: '20px',
              ...theme.typography.body2,
            }),
          }),
          sx
        )}
      >
        {calculatedShowIcon && (
          <BadgeIcon
            sx={(theme) => ({
              color: theme.featureFlags.panwRebrandStubYear ? fill : undefined,
              ...theme.typography.body1,
            })}
          />
        )}
        {label}
      </Box>
    );

  if (TooltipContent) {
    return <Tooltip Content={TooltipContent}>{badge}</Tooltip>;
  }
  return badge;
});

function useVariantProps(variant: BadgeVariant) {
  const { theme } = useTheme();
  const iconVariantMap: Record<BadgeVariant, React.ElementType<SvgIconProps>> = {
    default: Star,
    new: CheckDecagramOutline,
    info: InformationOutline,
    success: CheckCircle,
    warning: Alert,
    error: AlertDecagram,
  };

  const VariantIcon = iconVariantMap[variant];
  const badgeColors = theme.palette.badge[variant];

  return {
    VariantIcon,
    background: badgeColors.background,
    color: badgeColors.text,
    fill: badgeColors.fill,
  };
}
